import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./HistoryModal.css";
import { FaSearch } from "react-icons/fa";
import { toZoneDateTimeWithSeconds } from "./DateUtils";

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  params: {
    history_uid: string;
  };
}

interface HistoryEntry {
  history_uid: string;
  operation: string;
  timestamp: string;
  history_text: string;
  resource: string;
  updated_by: string;
}

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  params,
}) => {
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  const filterRef = useRef<HTMLDivElement>(null);

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setSortField("");
      setFilterType("");
      setFilterValue("");
      setShowFilterModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilterModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const historyUid = params?.history_uid;
    if (isOpen && historyUid) {
      const fetchHistory = async () => {
        try {
          const response = await axios.get(`${beAddress}/api/update-history`, {
            headers: {
              "user-uid": userUid,
            }, withCredentials: true,
            params: { history_uid: historyUid },
          });

          const apiData = response.data;

          if (apiData.status === "Success" && Array.isArray(apiData.data)) {
            setHistoryData(apiData.data);
          } else {
            setHistoryData([]);
            console.error(
              "History fetch failed:",
              apiData.message || "Unknown error"
            );
          }
        } catch (err) {
          console.error("Failed to fetch history:", err);
          setHistoryData([]);
        }
      };

      fetchHistory();
    } else {
      setHistoryData([]);
    }
  }, [isOpen, params.history_uid, sessionUid, userUid]);

  const uniqueUpdatedBy = Array.from(
    new Set(historyData.map((entry) => entry.updated_by))
  );

  const getSortedData = (data: HistoryEntry[]): HistoryEntry[] => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();

      switch (sortField) {
        case "timestamp_desc":
          return dateB - dateA;
        case "timestamp_asc":
          return dateA - dateB;
        default:
          return 0;
      }
    });
  };

  const getFilteredData = (): HistoryEntry[] => {
    const filtered = historyData.filter((entry) => {
      const matchesSearchTerm =
        entry.history_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.updated_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.timestamp.includes(searchTerm);

      const matchesFilter =
        !filterType ||
        (filterType === "operation" &&
          entry.operation.toLowerCase().includes(filterValue.toLowerCase())) ||
        (filterType === "updated_by" &&
          entry.updated_by.toLowerCase().includes(filterValue.toLowerCase()));

      return matchesSearchTerm && matchesFilter;
    });

    return getSortedData(filtered);
  };

  if (!isOpen) return null;

  const filteredSortedData = getFilteredData();

  return (
    <div className="historymodal-overlay">
      <div className="historymodal-content">
        <div className="historymodal-header">
          <h3>View History Details</h3>
          <button onClick={onClose} className="historymodal-close-button">
            ✖
          </button>
        </div>

        <div className="historymodal-controls">
          <div className="historymodal-search-wrapper">
            <FaSearch className="historymodal-search-icon" />
            <input
              type="text"
              placeholder="Search history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="historymodal-search"
            />
          </div>

          <select
            className="historymodal-sort-dropdown"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
          >
            <option value="timestamp_desc">Newest First</option>
            <option value="timestamp_asc">Oldest First</option>
          </select>

          <select
            className="historymodal-sort-dropdown"
            value={filterValue}
            onChange={(e) => {
              setFilterType("operation");
              setFilterValue(e.target.value);
            }}
          >
            <option value="">All Operations</option>
            <option value="add">Add</option>
            <option value="update">Update</option>
          </select>
        </div>

        <div className="historymodal-body">
          {filteredSortedData.length > 0 ? (
            filteredSortedData.map((entry, idx) => (
              <div key={idx} className="history-entry">
                <div className="history-entry-header">
                  <span className="history-operation">
                    {entry.operation.toUpperCase()}
                  </span>
                  <span className="history-timestamp">
                    {toZoneDateTimeWithSeconds(entry.timestamp)}
                  </span>
                </div>
                <p className="history-text">{entry.history_text}</p>
                <div className="history-updated-by">By: {entry.updated_by}</div>
              </div>
            ))
          ) : (
            <p>No history data found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
